import React from "react";
import { Modal, Button, Divider, Tag, Space } from "antd";
import { CloseOutlined, PhoneOutlined } from "@ant-design/icons";
import { ReactComponent as WhatsAppIcon } from "../../../assets/images/icons/whatsappGreenEmoji.svg";

const BusinessDetailsModal = ({ visible, business, activeTab, onCancel }) => {
  if (!business) return null;

  const shouldShowRestaurant = activeTab === "restaurant" || !activeTab;
  const shouldShowStore = activeTab === "store" || !activeTab;
  const shouldShowRawFood = activeTab === "raw_food" || !activeTab;

  const handleWhatsApp = (phoneNumber) => {
    if (phoneNumber) {
      const cleanPhone = phoneNumber.replace(/^\+/, "");
      window.open(`https://wa.me/${cleanPhone}`, "_blank");
    }
  };

  return (
    <Modal
      title={`Business Details - ${business.name}`}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
      closeIcon={<CloseOutlined />}
      style={{ maxHeight: "90vh" }}
      bodyStyle={{ maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}
    >
      <div className="space-y-6">
        {/* Status & Basic Info */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-sm text-gray-600 font-semibold mb-1">
                Status:
              </p>
              <Tag color={business.status === "active" ? "green" : "red"}>
                {business.status.toUpperCase()}
              </Tag>
            </div>
            <div>
              <p className="text-sm text-gray-600 font-semibold mb-2">
                Business Types:
              </p>
              <div className="flex flex-wrap gap-2">
                {business.businessTypes?.map((type) => (
                  <Tag key={type} color="blue">
                    {type.replace("_", " ").toUpperCase()}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Divider />

        {/* Main Business Info */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Business Information
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-blue-700 font-medium">Name:</span>
              <span className="ml-2 text-gray-900">{business.name}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Email:</span>
              <span className="ml-2 text-gray-900">
                {business.businessEmail}
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Telephone:</span>
              <span className="ml-2">
                <Space size="small">
                  <span className="text-gray-900">
                    {business.businessTelephone
                      ? "+234" + business.businessTelephone
                      : "N/A"}
                  </span>
                  {business.businessTelephone && (
                    <>
                      <Button
                        type="link"
                        size="small"
                        icon={<PhoneOutlined />}
                        href={`tel:+234${business.businessTelephone}`}
                        style={{ padding: 0 }}
                      />
                      <Button
                        type="link"
                        size="small"
                        icon={
                          <WhatsAppIcon style={{ width: 14, height: 14 }} />
                        }
                        onClick={() =>
                          handleWhatsApp("234" + business.businessTelephone)
                        }
                        title="Chat on WhatsApp"
                        style={{ padding: 0 }}
                      />
                    </>
                  )}
                </Space>
              </span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Address:</span>
              <span className="ml-2 text-gray-900">{business.address}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">State/LGA:</span>
              <span className="ml-2 text-gray-900">
                {business.state} / {business.lga}
              </span>
            </div>
          </div>
        </div>

        {/* Restaurant Details */}
        {shouldShowRestaurant && business.restaurant && (
          <>
            <Divider />
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-900 mb-3">
                🍽️ Restaurant Details
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-orange-700 font-medium">Name:</span>
                  <span className="ml-2 text-gray-900">
                    {business.restaurant.name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-orange-700 font-medium">Address:</span>
                  <span className="ml-2 text-gray-900">
                    {business.restaurant.address || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-orange-700 font-medium">
                    Telephone:
                  </span>
                  <span className="ml-2">
                    <Space size="small">
                      <span className="text-gray-900">
                        {business.restaurant.contactTelephone || "—"}
                      </span>
                      {business.restaurant.contactTelephone && (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon={<PhoneOutlined />}
                            href={`tel:${business.restaurant.contactTelephone}`}
                            style={{ padding: 0 }}
                          />
                          <Button
                            type="link"
                            size="small"
                            icon={
                              <WhatsAppIcon style={{ width: 14, height: 14 }} />
                            }
                            onClick={() =>
                              handleWhatsApp(
                                business.restaurant.contactTelephone,
                              )
                            }
                            title="Chat on WhatsApp"
                            style={{ padding: 0 }}
                          />
                        </>
                      )}
                    </Space>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Raw Food Details */}
        {shouldShowRawFood && business.rawFood && (
          <>
            <Divider />
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">
                🥬 Wholesale Details
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-purple-700 font-medium">Address:</span>
                  <span className="ml-2 text-gray-900">
                    {business.rawFood.address || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-purple-700 font-medium">
                    Telephone:
                  </span>
                  <span className="ml-2">
                    <Space size="small">
                      <span className="text-gray-900">
                        {business.rawFood.contactTelephone || "—"}
                      </span>
                      {business.rawFood.contactTelephone && (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon={<PhoneOutlined />}
                            href={`tel:${business.rawFood.contactTelephone}`}
                            style={{ padding: 0 }}
                          />
                          <Button
                            type="link"
                            size="small"
                            icon={
                              <WhatsAppIcon style={{ width: 14, height: 14 }} />
                            }
                            onClick={() =>
                              handleWhatsApp(business.rawFood.contactTelephone)
                            }
                            title="Chat on WhatsApp"
                            style={{ padding: 0 }}
                          />
                        </>
                      )}
                    </Space>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Store Details */}
        {shouldShowStore && business.store && (
          <>
            <Divider />
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-900 mb-3">
                🏪 Store Details
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-green-700 font-medium">Name:</span>
                  <span className="ml-2 text-gray-900">
                    {business.store.name || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Address:</span>
                  <span className="ml-2 text-gray-900">
                    {business.store.address || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Telephone:</span>
                  <span className="ml-2">
                    <Space size="small">
                      <span className="text-gray-900">
                        {business.store.contactTelephone || "—"}
                      </span>
                      {business.store.contactTelephone && (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon={<PhoneOutlined />}
                            href={`tel:${business.store.contactTelephone}`}
                            style={{ padding: 0 }}
                          />
                          <Button
                            type="link"
                            size="small"
                            icon={
                              <WhatsAppIcon style={{ width: 14, height: 14 }} />
                            }
                            onClick={() =>
                              handleWhatsApp(business.store.contactTelephone)
                            }
                            title="Chat on WhatsApp"
                            style={{ padding: 0 }}
                          />
                        </>
                      )}
                    </Space>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Support Officer Details */}
        {business.supportOfficer && (
          <>
            <Divider />
            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
              <h3 className="text-lg font-semibold text-cyan-900 mb-3">
                👤 Support Officer
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-cyan-700 font-medium">Name:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportOfficer.name}
                  </span>
                </div>
                <div>
                  <span className="text-cyan-700 font-medium">Email:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportOfficer.email || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-cyan-700 font-medium">Phone:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportOfficer.phoneNumber || "—"}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {business.supportManager && (
          <>
            <Divider />
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h3 className="text-lg font-semibold text-indigo-900 mb-3">
                🎯 Support Manager
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-indigo-700 font-medium">Name:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportManager.name}
                  </span>
                </div>
                <div>
                  <span className="text-indigo-700 font-medium">Email:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportManager.email || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-indigo-700 font-medium">Phone:</span>
                  <span className="ml-2 text-gray-900">
                    {business.supportManager.phoneNumber || "—"}
                  </span>
                </div>
                {business.supportManager.assignedAt && (
                  <div>
                    <span className="text-indigo-700 font-medium">
                      Assigned:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {new Date(
                        business.supportManager.assignedAt,
                      ).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* Metadata */}
        <div className="text-gray-500">
          <div>Created: {new Date(business.createdAt).toLocaleString()}</div>
          <div>
            Last Updated: {new Date(business.updatedAt).toLocaleString()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BusinessDetailsModal;
